import {
  ALLOWED_PDF_EXTENSIONS,
  MAX_PDF_SIZE_BYTES,
} from "@etudescarre/constants";
import { Button, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { todayLocal } from "@/lib/utils/date";
import { useYardContext } from "@/providers/yard-context";
import { FieldError } from "../field-error";
import { ReportDraftButtons, useReportDrafts } from "./report-drafts";
import { ReportEntryStep } from "./report-entry-step";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TeamMember = {
  employeeId: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
};

type YardContract = {
  id: number;
  code: string | null;
  type: string;
  performerCompanyName: string | null;
  isActive: boolean;
  teams: Array<{ members: TeamMember[] }>;
};

type ReportFormData = {
  emetteurContractId: number;
  numero: string;
  dateReception: string;
  dateRedaction: string;
  authorEmployeeId: string;
};

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";

const labelClass =
  "mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100";

function memberLabel(m: TeamMember): string {
  const name = [m.firstName, m.lastName].filter(isNotNil).join(" ").trim();
  return name.length > 0 ? name : m.employeeId;
}

type RegisterReportPanelProps = {
  onClose: () => void;
};

export function RegisterReportPanel({ onClose }: RegisterReportPanelProps) {
  const { yard, contracts: myContracts } = useYardContext();

  // All yard contracts (the emetteur is usually a DO the user does not pilot,
  // so we cannot rely on the piloted-contracts list from the yard context).
  const [allContracts, setAllContracts] = useState<YardContract[]>([]);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Empty required fields are only flagged once « Enregistrer » was clicked.
  const [triedSubmit, setTriedSubmit] = useState(false);
  // Une fois le rapport enregistré (brouillon), ou rouvert depuis son n°, on
  // reste sur le panneau pour saisir les avis puis valider la saisie.
  const [openedReport, setOpenedReport] = useState<{
    id: number;
    hasPdf: boolean;
  } | null>(null);

  // Contracts related to my interventions get highlighted: mine, and the
  // donneurs d'ordre attached to the buildings of the interventions I pilot.
  const [linkedContractIds, setLinkedContractIds] = useState<number[]>([]);
  const starredContractIds = useMemo(
    () => new Set([...myContracts.map((c) => c.id), ...linkedContractIds]),
    [myContracts, linkedContractIds],
  );

  const form = useForm<ReportFormData>({
    defaultValues: {
      emetteurContractId: 0,
      numero: "",
      dateReception: todayLocal(),
      dateRedaction: todayLocal(),
      authorEmployeeId: "",
    },
  });

  const selectedContractId = form.watch("emetteurContractId");
  const selectedAuthor = form.watch("authorEmployeeId");
  const dateReception = form.watch("dateReception");
  const dateRedaction = form.watch("dateRedaction");
  const numero = form.watch("numero");

  const errors = {
    contract:
      triedSubmit && selectedContractId <= 0
        ? "Sélectionnez un contrat émetteur"
        : null,
    numero:
      triedSubmit && numero.trim().length === 0
        ? "Renseignez le n° du rapport"
        : null,
    dateReception:
      triedSubmit && dateReception.length === 0
        ? "Renseignez la date de réception"
        : null,
    // Dates in YYYY-MM-DD order as strings. Flagged at once, while typing.
    dateRedaction:
      dateReception.length > 0 && dateRedaction > dateReception
        ? "La date de rédaction doit être antérieure ou égale à la date de réception"
        : triedSubmit && dateRedaction.length === 0
          ? "Renseignez la date de rédaction"
          : null,
    author:
      triedSubmit && selectedAuthor.length === 0
        ? "Sélectionnez un auteur"
        : null,
  };

  const drafts = useReportDrafts(
    selectedContractId > 0 ? selectedContractId : null,
  );
  const typedDraft = drafts.find((draft) => draft.numero === numero.trim());

  useEffect(() => {
    let cancelled = false;
    async function fetchContracts() {
      const { data } = await apiClient.GET("/api/yards/{yardId}/contracts", {
        params: { path: { yardId: String(yard.id) } },
      });
      if (!cancelled && isNotNil(data)) {
        setAllContracts(data.data.contracts as YardContract[]);
      }
    }
    async function fetchLinkedContracts() {
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/diffusion-queue/recipients",
        {
          params: {
            path: { yardId: String(yard.id) },
            query: { target: "do" },
          },
        },
      );
      if (!cancelled && isNotNil(data)) {
        setLinkedContractIds(data.data.recipients.map((r) => r.contractId));
      }
    }
    fetchContracts();
    fetchLinkedContracts();
    return () => {
      cancelled = true;
    };
  }, [yard.id]);

  // Emetteur options: only active contracts, piloted ones first + flagged.
  const contractOptions = useMemo(() => {
    return allContracts
      .filter((c) => c.isActive)
      .map((c) => ({
        value: String(c.id),
        label: `${starredContractIds.has(c.id) ? "★ " : ""}${
          c.performerCompanyName ?? c.code ?? c.type
        }${c.code ? ` (${c.code})` : ""}`,
      }))
      .sort((a, b) => {
        const aStar = a.label.startsWith("★");
        const bStar = b.label.startsWith("★");
        if (aStar === bStar) return a.label.localeCompare(b.label);
        return aStar ? -1 : 1;
      });
  }, [allContracts, starredContractIds]);

  // Author options: active team members of the selected emetteur contract.
  const authorOptions = useMemo(() => {
    const contract = allContracts.find((c) => c.id === selectedContractId);
    if (isNil(contract)) return [];
    const seen = new Set<string>();
    const members: TeamMember[] = [];
    for (const team of contract.teams) {
      for (const m of team.members) {
        if (m.isActive && !seen.has(m.employeeId)) {
          seen.add(m.employeeId);
          members.push(m);
        }
      }
    }
    return members.map((m) => ({ value: m.employeeId, label: memberLabel(m) }));
  }, [allContracts, selectedContractId]);

  const handlePdfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (isNil(file)) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (
      isNil(ext) ||
      !ALLOWED_PDF_EXTENSIONS.includes(
        ext as (typeof ALLOWED_PDF_EXTENSIONS)[number],
      )
    ) {
      toaster.error({
        title: "Erreur",
        description: "Le fichier doit être au format PDF",
      });
      e.target.value = "";
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      toaster.error({
        title: "Erreur",
        description: "Le fichier PDF dépasse 50 Mo",
      });
      e.target.value = "";
      return;
    }
    setPdfFile(file);
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    if (isNotNil(typedDraft)) {
      setOpenedReport(typedDraft);
      return;
    }
    if (
      data.emetteurContractId <= 0 ||
      data.numero.trim().length === 0 ||
      data.dateReception.length === 0 ||
      data.dateRedaction.length === 0 ||
      data.dateRedaction > data.dateReception ||
      data.authorEmployeeId.length === 0
    ) {
      return;
    }

    setIsSubmitting(true);
    try {
      const metadata = {
        emetteurContractId: data.emetteurContractId,
        numero: data.numero,
        dateReception: new Date(data.dateReception).toISOString(),
        dateRedaction: new Date(data.dateRedaction).toISOString(),
        authorEmployeeId: data.authorEmployeeId,
      };

      const { data: created, error } = await apiClient.POST(
        "/api/yards/{yardId}/reports",
        {
          params: { path: { yardId: String(yard.id) } },
          body: {
            metadata: JSON.stringify(metadata),
            pdf: pdfFile ?? undefined,
          },
          bodySerializer(body) {
            const fd = new FormData();
            for (const [key, value] of Object.entries(
              body as Record<string, unknown>,
            )) {
              if (value instanceof File) {
                fd.append(key, value, value.name);
              } else if (isNotNil(value)) {
                fd.append(key, String(value));
              }
            }
            return fd;
          },
        },
      );

      if (error || isNil(created)) {
        toaster.error({
          title: "Erreur",
          description: error?.error ?? "Échec de l'enregistrement",
        });
        return;
      }

      toaster.success({
        title: "Succès",
        description: "Rapport enregistré en brouillon",
      });
      // On passe à la saisie des avis puis à la validation, sans
      // fermer le panneau. Fermer le panneau conserve le brouillon.
      setOpenedReport({
        id: created.data.reportId,
        hasPdf: isNotNil(pdfFile),
      });
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSubmitting(false);
    }
  });

  // Étape 2 — le rapport est en brouillon ; saisie des avis puis
  // validation. Fermer le panneau à ce stade conserve le brouillon.
  if (isNotNil(openedReport)) {
    return (
      <ReportEntryStep
        reportId={openedReport.id}
        hasPdf={openedReport.hasPdf}
        intro="Le rapport est enregistré en brouillon. Ajoutez les avis figurant dans le rapport puis validez la saisie. À la validation, les contrats auteurs des documents visés sont notifiés par mail."
        onClose={onClose}
      />
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onClose}
        className="text-sm text-blue-600 hover:underline dark:text-blue-400"
      >
        Retour aux actions
      </button>

      {/* noValidate: the browser's bubble is in its own language and shows
          nothing next to the field. */}
      <form
        noValidate
        onSubmit={(event) => {
          setTriedSubmit(true);
          void handleSubmit(event);
        }}
        className="space-y-4"
      >
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          Rapport d'examen reçu
        </h3>

        <div>
          <span className={labelClass}>
            Contrat émetteur <span className="text-red-500">*</span>
          </span>
          <Select
            options={contractOptions}
            value={selectedContractId > 0 ? [String(selectedContractId)] : []}
            onValueChange={(details) => {
              const val = details.value[0];
              form.setValue("emetteurContractId", val ? Number(val) : 0);
              form.setValue("authorEmployeeId", "");
            }}
            placeholder="Sélectionnez un contrat"
          />
          <p className="mt-1 text-xs text-zinc-500">
            ★ contrats liés à vos interventions
          </p>
          <FieldError message={errors.contract} />
        </div>

        <div>
          <label htmlFor="reportNumero" className={labelClass}>
            N° rapport <span className="text-red-500">*</span>
          </label>
          <input
            id="reportNumero"
            type="text"
            {...form.register("numero")}
            required
            minLength={1}
            maxLength={255}
            aria-invalid={isNotNil(errors.numero)}
            className={inputClass}
          />
          <FieldError message={errors.numero} />
          <ReportDraftButtons drafts={drafts} onPick={setOpenedReport} />
        </div>

        <div>
          <label htmlFor="dateReception" className={labelClass}>
            Date de réception <span className="text-red-500">*</span>
          </label>
          <input
            id="dateReception"
            type="date"
            {...form.register("dateReception")}
            required
            aria-invalid={isNotNil(errors.dateReception)}
            className={inputClass}
          />
          <FieldError message={errors.dateReception} />
        </div>

        <div>
          <label htmlFor="dateRedaction" className={labelClass}>
            Date de rédaction <span className="text-red-500">*</span>
          </label>
          <input
            id="dateRedaction"
            type="date"
            {...form.register("dateRedaction")}
            required
            max={dateReception}
            aria-invalid={isNotNil(errors.dateRedaction)}
            className={inputClass}
          />
          <FieldError message={errors.dateRedaction} />
        </div>

        <div>
          <span className={labelClass}>
            Auteur du rapport <span className="text-red-500">*</span>
          </span>
          <Select
            options={authorOptions}
            value={selectedAuthor ? [selectedAuthor] : []}
            onValueChange={(details) => {
              form.setValue("authorEmployeeId", details.value[0] ?? "");
            }}
            placeholder={
              selectedContractId > 0
                ? "Sélectionnez un membre d'équipe"
                : "Sélectionnez d'abord un contrat émetteur"
            }
          />
          <FieldError message={errors.author} />
        </div>

        <div>
          <label htmlFor="reportPdf" className={labelClass}>
            Rapport (PDF)
          </label>
          <input
            id="reportPdf"
            type="file"
            accept=".pdf"
            onChange={handlePdfChange}
            className="w-full text-sm text-zinc-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400"
          />
          {isNotNil(pdfFile) && (
            <p className="mt-1 text-xs text-zinc-500">
              {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(1)} Mo)
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            outline
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? "En cours..."
              : isNotNil(typedDraft)
                ? "Reprendre le brouillon"
                : "Enregistrer"}
          </Button>
        </div>
      </form>
    </div>
  );
}
